import { useEffect } from "react"

import { usePixelEvent } from "./usePixelEvent"
import {
  ANALYTIC_EVENTS,
  FacebookPixelEvent,
  GAEvent,
  PinterestPixelEvent,
  TiktokPixelEvent,
  trackAnalyticEvent,
  trackGAEvent,
} from "../util/analytics"
import { useRouter } from "../util/router"

// Session-local flag so the funnel-entry attribution events fire at most once
// per browser session, regardless of which entry route the user lands on
// first. Without this, a user going /coach-welcome → /coach-onboarding would
// fire CONTACT pixels twice.
const FUNNEL_ENTRY_FIRED_FLAG = "joon.coach_funnel_entry_attribution_fired"

/**
 * Fires the cross-domain attribution events that close the loop between the
 * jooncoach.com ad → my.joonapp.io coach-funnel hop. Specifically:
 *   - Facebook / TikTok / Pinterest CONTACT pixel events (Meta + TikTok +
 *     Pinterest top-of-funnel conversion signals)
 *   - Amplitude `SHOW_WELCOME` event (carries utm_* + focus + entry_route)
 *   - Google Ads `START_QUIZ` conversion (via trackGAEvent, which has its
 *     own per-session sessionStorage dedup)
 *
 * Historically this fired only on the /coach-welcome route. After Isaac asked
 * us to route landing-page CTAs straight to /coach-onboarding, we extracted
 * the firing into this hook and call it from BOTH /coach-welcome and
 * /coach-onboarding so attribution works for either entry route. The
 * sessionStorage flag prevents double-firing the pixels when a user
 * happens to traverse both routes (e.g., bookmarks /coach-welcome and then
 * clicks "Get started").
 *
 * `entryRoute` is logged into the Amplitude `SHOW_WELCOME` payload so the
 * analytics team can compare conversion rates between welcome-first and
 * onboarding-first landings.
 */
export function useCoachFunnelEntryAttribution(
  entryRoute: "welcome" | "onboarding"
) {
  const router = useRouter()
  const { trackFBPixelEvent, trackTikTokPixelEvent, trackPinterestEvent } =
    usePixelEvent()

  useEffect(() => {
    if (typeof window === "undefined") return

    // Dedup across the welcome ↔ onboarding hop within a single session.
    // trackGAEvent has its own GA-level sessionStorage dedup, but the pixel
    // helpers don't — guard them here.
    const alreadyFired = window.sessionStorage.getItem(FUNNEL_ENTRY_FIRED_FLAG)
    if (alreadyFired) return
    window.sessionStorage.setItem(FUNNEL_ENTRY_FIRED_FLAG, "1")

    trackFBPixelEvent(FacebookPixelEvent.CONTACT)
    trackTikTokPixelEvent(TiktokPixelEvent.CONTACT)
    trackPinterestEvent(PinterestPixelEvent.CONTACT)

    trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_WELCOME, {
      utm_source: router.query.utm_source,
      utm_campaign: router.query.utm_campaign,
      utm_content: router.query.utm_content,
      utm_term: router.query.utm_term,
      ad_set_name: router.query.ad_set_name,
      ad_name: router.query.ad_name,
      focus: router.query.focus,
      entry_route: entryRoute,
    })

    trackGAEvent(GAEvent.START_QUIZ)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
